import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import api from '../api';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Divider,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  Chip,
  Stack,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

export default function InventoryManager({ userId }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [amount, setAmount] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    inventoryId: null,
    itemId: null,
    itemName: null
  });

  // Get user data
  const { data: user } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  // Fetch all assets for local cache
  const { data: allAssets = [], isLoading: assetsLoading } = useQuery({
    queryKey: ['allAssets'],
    queryFn: async () => {
      const response = await api.get('/inventories/items');
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  // Build a Map for fast asset lookup by id
  const assetMap = useMemo(() => {
    const map = new Map();
    for (const asset of allAssets) {
      map.set(asset.id, asset);
    }
    return map;
  }, [allAssets]);

  // Fetch inventories
  const { data: inventories = [], isLoading: inventoriesLoading } = useQuery({
    queryKey: ['inventories', user?.id],
    queryFn: async () => {
      const response = await api.get(`/users/${user?.id}/inventories`);
      return response.data;
    },
    enabled: !!user?.id
  });

  // Fetch items for dropdown with search
  const { data: items = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['items', inputValue],
    queryFn: async () => {
      const response = await api.get(`/inventories/items${inputValue ? `?search=${encodeURIComponent(inputValue)}` : ''}`);
      return response.data;
    },
    enabled: true,
    staleTime: 30000
  });

  // Fetch items for each inventory using useQueries
  const inventoryItemsQueries = useQueries({
    queries: (inventories || []).map(inv => ({
      queryKey: ['inventoryItems', inv.id],
      queryFn: async () => {
        const response = await api.get(`/inventories/${inv.id}/items`);
        return response.data;
      },
      enabled: !!inv.id
    }))
  });

  // Find inventory ID based on asset's inventoryType
  const findInventoryIdFromAsset = (asset) => {
    if (!asset || !asset.inventoryType || !inventories || inventories.length === 0) return null;
    
    // Look for an inventory that matches the asset's inventoryType
    const matchingInventory = inventories.find(inv => inv.type === asset.inventoryType);
    if (matchingInventory) return matchingInventory.id;
    
    // If no specific match, use the first inventory as default
    return inventories[0]?.id || null;
  };

  const handleAddItem = async () => {
    if (!value || amount < 0 || amount > 100000) return;
    
    // Get the right inventory ID from the asset
    const inventoryId = findInventoryIdFromAsset(value);
    if (!inventoryId) {
      setSnackbar({ open: true, message: 'No valid inventory found for this item', severity: 'error' });
      return;
    }
    
    try {
      await api.post(`/inventories/${inventoryId}/items`, {
        assetId: value.id,
        amount: Number(amount),
        durability: 100 // or allow user to set
      });
      setSnackbar({ open: true, message: 'Item added successfully!', severity: 'success' });
      queryClient.invalidateQueries(['inventoryItems', inventoryId]);
      // Reset form after successful addition
      setValue(null);
      setInputValue('');
      setAmount(1);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to add item', severity: 'error' });
    }
  };

  const handleDeleteItem = async (inventoryId, itemId) => {
    try {
      await api.delete(`/inventories/${inventoryId}/items/${itemId}`);
      setSnackbar({ open: true, message: 'Item removed successfully!', severity: 'success' });
      queryClient.invalidateQueries(['inventoryItems', inventoryId]);
      setDeleteConfirmation({ open: false, inventoryId: null, itemId: null, itemName: null });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to remove item', severity: 'error' });
      setDeleteConfirmation({ open: false, inventoryId: null, itemId: null, itemName: null });
    }
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.inventoryId && deleteConfirmation.itemId) {
      handleDeleteItem(deleteConfirmation.inventoryId, deleteConfirmation.itemId);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ open: false, inventoryId: null, itemId: null, itemName: null });
  };

  // Helper to get asset info by id
  const getAssetById = (id) => assetMap.get(id);

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" mt={4} sx={{ p: 2 }}>
        <Typography variant="h6">No user selected. Please select a user from the User Search.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Inventory Management</Typography>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3, width: '100%' }}>
        <Typography variant="h6" gutterBottom>
          Add Item to Inventory
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Autocomplete
                options={items}
                getOptionLabel={(option) => option.title || option.name || ''}
                value={value}
                onChange={(event, newValue) => {
                  setValue(newValue);
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                loading={itemsLoading}
                filterOptions={(options, { inputValue }) => {
                  // Filter options based on input value
                  return options.filter(option => 
                    (option.title || option.name || '').toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => {
                  let itemType = 'Unknown';
                  let stackSize = option.stackSize;
                  let inventoryType = '';
                  let isNft = false;
                  
                  if (option.config) {
                    try {
                      const configObj = typeof option.config === 'string' ? JSON.parse(option.config) : option.config;
                      if (configObj && configObj.type) {
                        itemType = configObj.type;
                      }
                      if (configObj && configObj.stackSize !== undefined) {
                        stackSize = configObj.stackSize;
                      }
                      if (configObj && configObj.inventoryType) {
                        inventoryType = configObj.inventoryType;
                      }
                      if (configObj && configObj.isNft !== undefined) {
                        isNft = configObj.isNft;
                      }
                    } catch (configError) {
                      // Invalid config JSON, skip
                      console.warn('Failed to parse config JSON:', configError);
                    }
                  }
                  
                  return (
                    <li {...props} key={option.id}>
                      <Stack direction="row" alignItems="center" spacing={1} width="100%">
                        <Box sx={{ flex: 1 }}>
                          <Typography>{option.title || option.name}</Typography>
                        </Box>
                        {typeof stackSize !== 'object' && stackSize !== undefined && stackSize !== null && stackSize !== 0 && (
                          <Chip 
                            label={stackSize} 
                            size="small" 
                            color="default" 
                            sx={{ 
                              fontWeight: 600, 
                              bgcolor: 'grey.800', 
                              color: 'grey.200',
                              borderRadius: '4px',
                            }} 
                          />
                        )}
                        <Chip 
                          label={itemType} 
                          size="small" 
                          color="primary" 
                          sx={{ 
                            fontWeight: 600,
                            borderRadius: '4px',
                          }} 
                        />
                        {inventoryType && (
                          <Chip 
                            label={inventoryType} 
                            size="small" 
                            color="secondary" 
                            sx={{ 
                              fontWeight: 600,
                              borderRadius: '4px',
                            }} 
                          />
                        )}
                        {isNft && (
                          <Chip 
                            label="NFT" 
                            size="small" 
                            variant="outlined" 
                            sx={{ 
                              fontWeight: 600,
                              borderRadius: '4px',
                            }} 
                          />
                        )}
                      </Stack>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Search Items"
                    placeholder="Type to search for items..."
                    size="large"
                  />
                )}
                sx={{ 
                  minWidth: 400,
                  flex: 2,
                  '@media (max-width: 600px)': {
                    minWidth: '100%',
                  },
                  '& .MuiAutocomplete-listbox': {
                    maxHeight: 300,
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    minHeight: '56px',
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    paddingTop: '12px',
                    paddingBottom: '12px',
                  }
                }}
              />
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ min: 1, max: 100000 }}
                size="large"
                sx={{
                  maxWidth: 120,
                  fontSize: '1.1rem',
                  minHeight: '56px',
                }}
              />
              <Button
                variant="contained"
                disabled={!value || amount < 1 || amount > 100000}
                onClick={handleAddItem}
                size="large"
                sx={{ 
                  minWidth: 120,
                  minHeight: '56px',
                  fontSize: '1rem',
                }}
              >
                Add Item
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Browse Inventories</Typography>
      <Grid container spacing={4}>
        {inventories.map((inv, idx) => {
          const itemsQuery = inventoryItemsQueries[idx] || {};
          const inventoryItems = itemsQuery.data || [];
          return (
            <Grid item xs={12} lg={6} key={`inv-${inv.id}`}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardHeader 
                  title={inv.type || 'Inventory'} 
                  subheader={`ID: ${inv.id}`}
                />
                <Divider />
                <CardContent sx={{ p: 3 }}>
                  {itemsQuery.isLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>Loading items...</Typography>
                    </Box>
                  ) : inventoryItems.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No items in this inventory</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3}>
                      {inventoryItems.map(item => {
                        const asset = getAssetById(item.assetId);
                        return (
                          <Grid item xs={6} sm={4} lg={3} key={`item-${item.id}`}>
                            <Paper
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                minHeight: 140,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                backgroundColor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                  borderColor: 'primary.main',
                                }
                              }}
                              elevation={1}
                            >
                              <IconButton
                                size="small"
                                onClick={() => setDeleteConfirmation({
                                  open: true,
                                  inventoryId: inv.id,
                                  itemId: item.id,
                                  itemName: asset?.title || item.title || 'Unknown Item'
                                })}
                                aria-label="Delete item"
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                  color: 'error.main',
                                  zIndex: 2,
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 0, 0, 0.2)',
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>

                              <Badge
                                badgeContent={item.amount}
                                color="primary"
                                anchorOrigin={{
                                  vertical: 'top',
                                  horizontal: 'right',
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  zIndex: 2,
                                  '& .MuiBadge-badge': {
                                    fontSize: '0.75rem',
                                    minWidth: '20px',
                                    height: '20px',
                                  }
                                }}
                              >
                                <Box sx={{ width: 20, height: 20 }} />
                              </Badge>

                              <Box sx={{ textAlign: 'center', width: '100%', mt: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="bold"
                                  sx={{ 
                                    wordBreak: 'break-word',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.4,
                                    minHeight: '2.8em',
                                  }}
                                >
                                  {asset?.title || item.title || 'Unknown Item'}
                                </Typography>
                                {item.durability !== undefined && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Durability: {item.durability}%
                                  </Typography>
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteConfirmation.open}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{deleteConfirmation.itemName}" from the inventory?
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 